/**
 * Check if URL is a Cloudinary URL
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isCloudinaryUrl = (url) => {
  return url && (url.includes('cloudinary.com') || url.includes('res.cloudinary.com'));
};

/**
 * Optimize Cloudinary image URL with transformations
 * @param {string} url - Cloudinary URL
 * @param {Object} options - Transformation options
 * @param {number} options.width - Desired width
 * @param {number} options.height - Desired height
 * @param {string} options.crop - Crop mode (fill, limit, fit, scale, thumb)
 * @param {number|string} options.quality - Quality (auto, auto:best, auto:good, auto:eco, 80, etc.)
 * @param {string} options.format - Format (auto, webp, jpg, png)
 * @param {string} options.gravity - Gravity for cropping (face, auto, center, etc.)
 * @returns {string} Optimized Cloudinary URL
 */
export const optimizeCloudinaryUrl = (url, options = {}) => {
  if (!url || !isCloudinaryUrl(url)) {
    return url; // Return original if not Cloudinary URL
  }

  const {
    width,
    height,
    crop = 'limit', // 'limit' maintains aspect ratio, 'fill' crops to exact size
    quality = 'auto:good', // Auto quality with good balance
    format = 'auto', // Auto format (webp if supported)
    gravity = 'auto' // Auto gravity for face detection
  } = options;

  // Parse the Cloudinary URL
  // Format: https://res.cloudinary.com/{cloud_name}/image/upload/{transformations}/{public_id}.{format}
  const urlParts = url.split('/upload/');
  
  if (urlParts.length !== 2) {
    return url; // Invalid Cloudinary URL format
  }

  const baseUrl = urlParts[0] + '/upload/';
  const restOfUrl = urlParts[1];

  // Build transformation string
  const transformations = [];

  if (width || height) {
    let size = '';
    if (width && height) {
      size = `w_${width},h_${height},c_${crop}`;
      if (crop === 'fill' && gravity) {
        size += `,g_${gravity}`;
      }
    } else if (width) {
      size = `w_${width},c_limit`;
    } else if (height) {
      size = `h_${height},c_limit`;
    }
    if (size) {
      transformations.push(size);
    }
  }

  // Add quality
  if (quality) {
    transformations.push(`q_${quality}`);
  }

  // Add format
  if (format) {
    transformations.push(`f_${format}`);
  }

  // Build final URL
  if (transformations.length > 0) {
    return `${baseUrl}${transformations.join(',')}/${restOfUrl}`;
  }

  return url; // No transformations needed
};

/**
 * Get optimized product image URL
 * @param {string} imageUrl - Original image URL
 * @param {string} size - Size preset ('thumbnail', 'small', 'medium', 'large', 'original')
 * @returns {string} Optimized URL
 */
export const getOptimizedProductImage = (imageUrl, size = 'medium') => {
  if (!imageUrl) {
    return 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';
  }

  const sizePresets = {
    thumbnail: { width: 150, height: 150, crop: 'fill', quality: 'auto:good' },
    small: { width: 300, height: 300, crop: 'limit', quality: 'auto:good' },
    medium: { width: 600, height: 600, crop: 'limit', quality: 'auto:good' },
    large: { width: 1200, height: 1200, crop: 'limit', quality: 'auto:best' },
    original: {} // No transformations
  };

  const preset = sizePresets[size] || sizePresets.medium;

  if (isCloudinaryUrl(imageUrl)) {
    return optimizeCloudinaryUrl(imageUrl, preset);
  }

  return imageUrl; // Return original if not Cloudinary
};

/**
 * Generate srcset for responsive images
 * @param {string} imageUrl - Original image URL
 * @param {Array<number>} widths - Array of widths (e.g., [300, 600, 1200])
 * @param {string} format - Image format ('webp', 'auto', 'jpg')
 * @returns {string} srcset string
 */
export const generateSrcSet = (imageUrl, widths = [300, 600, 1200], format = 'auto') => {
  if (!imageUrl || !isCloudinaryUrl(imageUrl)) {
    return imageUrl; // Return original if not Cloudinary
  }

  return widths
    .map((width) => {
      const optimizedUrl = optimizeCloudinaryUrl(imageUrl, {
        width,
        crop: 'limit',
        quality: 'auto:good',
        format
      });
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
};

/**
 * Generate WebP srcset
 * @param {string} imageUrl - Original image URL
 * @param {Array<number>} widths - Array of widths
 * @returns {string} WebP srcset string
 */
export const generateWebPSrcSet = (imageUrl, widths = [300, 600, 1200]) => {
  return generateSrcSet(imageUrl, widths, 'webp');
};

